// @file Pending-mark state for a OneDrive paste upgrade.
// Shared by the boot entry and the upgrade job. It must not import either of those modules:
// the job is loaded only after the mark, and a cycle would pull the job's chunks back onto boot.
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import type { Cid } from '@@/types'

// The post editor hides a draft's generic link preview and shows its loading state while the draft
// carries a cloud picker source and an upload progress below 1 (see shouldKeepCloudPickerLoading).
// Marking the draft that way before the resolve request starts keeps the anonymous link preview,
// which for a private OneDrive/SharePoint link is a screenshot of Microsoft's sign-in page, from
// flashing up for the seconds it takes Graph to resolve and the upload to begin. The real upload
// takes over the progress in onStart; every other exit clears the mark so the plain link shows.
const PENDING_UPGRADE_PROGRESS = 0.01
const pendingUpgradeUrlByDraftCid = new Map<Cid, string>()

function markUpgradePending(draftCid: Cid, url: string): void {
  pendingUpgradeUrlByDraftCid.set(draftCid, url)
  useSurfaceDraftsStore().updateDraft({
    cid: draftCid,
    cloudPickerSource: 'microsoft_one_drive_picker',
    uploadProgress: PENDING_UPGRADE_PROGRESS,
  })
}

// Clears the pending mark unless a newer upgrade for the same draft owns it, or the user has since
// started a real upload (attachment changed and progress is no longer the pending sentinel).
function clearPendingUpgrade(draftCid: Cid, url: string): void {
  if (pendingUpgradeUrlByDraftCid.get(draftCid) !== url) return
  pendingUpgradeUrlByDraftCid.delete(draftCid)
  const draft = useSurfaceDraftsStore().draftByCid[draftCid]
  if (draft == null) return
  if (draft.attachment !== url && draft.uploadProgress !== PENDING_UPGRADE_PROGRESS) return
  useSurfaceDraftsStore().updateDraft({ cid: draftCid, cloudPickerSource: null, uploadProgress: undefined })
}

function releasePendingUpgrade(draftCid: Cid, url: string): void {
  if (pendingUpgradeUrlByDraftCid.get(draftCid) === url) pendingUpgradeUrlByDraftCid.delete(draftCid)
}

export { clearPendingUpgrade, markUpgradePending, releasePendingUpgrade }
